import { useState } from "react";
import { SmoothLink as Link } from "@/components/navigation";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { getEntityImageUrl, generateAltText } from "@/lib/image-utils";
import { ArrowRight, DollarSign, Stethoscope, Heart, Bone, Activity, Baby, Repeat, Eye, Sparkles, Leaf, Dumbbell, Brain } from "lucide-react";
import type { Treatment } from "@/types/schema";
import { cn } from "@/lib/utils";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

const categoryIcons: Record<string, React.ReactNode> = {
  dental: <Stethoscope className="w-8 h-8" />,
  cardiac: <Heart className="w-8 h-8" />,
  orthopedics: <Bone className="w-8 h-8" />,
  oncology: <Activity className="w-8 h-8" />,
  fertility: <Baby className="w-8 h-8" />,
  transplants: <Repeat className="w-8 h-8" />,
  ophthalmology: <Eye className="w-8 h-8" />,
  cosmetic: <Sparkles className="w-8 h-8" />,
  wellness: <Leaf className="w-8 h-8" />,
  rehabilitation: <Dumbbell className="w-8 h-8" />,
  mental_health: <Brain className="w-8 h-8" />,
};

interface TreatmentCardProps {
  treatment: Treatment;
}

export function TreatmentCard({ treatment }: TreatmentCardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { trackTreatmentView } = useAnalyticsContext();
  
  const altText = generateAltText(treatment.name, "treatment");

  const imageUrl = getEntityImageUrl(treatment.image_url, treatment.name, "medical", 400, 225);

  return (
    <Card className="group overflow-hidden hover:shadow-hover transition-all duration-300">
      <div className="relative overflow-hidden">
        <AspectRatio ratio={16 / 9}>
          {isLoading && (
            <Skeleton className="absolute inset-0 w-full h-full" />
          )}
          
          {!hasError ? (
            <img
              width={400}
              height={225}
              src={imageUrl}
              alt={altText}
              loading="lazy"
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
              className={cn(
                "w-full h-full object-cover group-hover:scale-105 transition-transform duration-300",
                isLoading ? "blur-placeholder opacity-0" : "blur-loaded opacity-100"
              )}
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                {categoryIcons[treatment.category] || <Stethoscope className="w-8 h-8" />}
              </div>
            </div>
          )}
        </AspectRatio>

        {treatment.is_featured && (
          <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">
            Featured
          </Badge>
        )}
      </div>

      <CardContent className="p-6">
        <Badge variant="outline" className="mb-3 capitalize">
          {treatment.category.replace("_", " ")}
        </Badge>
        
        <Link 
          to={`/treatments/${treatment.category}/${treatment.slug}`}
          onClick={() => trackTreatmentView(treatment.name, treatment.category)}
        >
          <h2 className="font-display text-xl font-semibold text-secondary mb-2 group-hover:text-primary transition-colors">
            {treatment.name}
          </h2>
        </Link>
        
        {treatment.description && (
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
            {treatment.description}
          </p>
        )}
        
        {/* Pricing */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-muted-foreground">India Price</p>
            {treatment.price_india_min && treatment.price_india_max ? (
              <p className="font-semibold text-primary flex items-center">
                <DollarSign className="w-4 h-4" />
                {treatment.price_india_min.toLocaleString()} - {treatment.price_india_max.toLocaleString()}
              </p>
            ) : (
              <p className="font-semibold text-primary text-sm mt-0.5">
                Get a Quote
              </p>
            )}
          </div>
          {treatment.savings_percentage ? (
            <Badge className="bg-accent/10 text-accent border-accent/20">
              Save {treatment.savings_percentage}%
            </Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground border-border">
              Detailed
            </Badge>
          )}
        </div>

        <Link 
          to={`/treatments/${treatment.category}/${treatment.slug}`}
          onClick={() => trackTreatmentView(treatment.name, treatment.category)}
        >
          <Button 
            variant="outline" 
            className="w-full rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
          >
            View Details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export function TreatmentCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <AspectRatio ratio={16 / 9}>
        <Skeleton className="w-full h-full" />
      </AspectRatio>
      <CardContent className="p-6 space-y-4">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-12 w-full" />
        <div className="flex justify-between">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-6 w-16" />
        </div>
        <Skeleton className="h-10 w-full" />
      </CardContent>
    </Card>
  );
}
